import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { CART_STORAGE_KEY, cacheProduct } from "./experienceUtils";

const ExperienceCartContext = createContext(null);

const loadCart = () => {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

const saveCart = (items) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    // ignore
  }
};

export function ExperienceCartProvider({ children }) {
  const [items, setItems] = useState(loadCart);

  useEffect(() => {
    saveCart(items);
  }, [items]);

  const addItem = (product, options) => {
    cacheProduct(product);
    const line = {
      lineId: `${product.productCode}-${Date.now()}`,
      productCode: product.productCode,
      title: product.title,
      image: options?.image || null,
      images:
        options?.images && options.images.length
          ? options.images
          : options?.image
          ? [options.image]
          : [],
      currency: options?.currency || (product.pricing && product.pricing.currency) || "USD",
      price:
        options?.price ??
        (product.pricing && product.pricing.summary && product.pricing.summary.fromPrice) ??
        0,
      date: options?.date || null,
      travelers: options?.travelers || 2,
      destinationName: options?.destinationName || "",
      freeCancellation:
        !!(product.flags && product.flags.includes("FREE_CANCELLATION")),
    };
    setItems((prev) => [...prev, line]);
    return line;
  };

  const removeItem = (lineId) => {
    setItems((prev) => prev.filter((it) => it.lineId !== lineId));
  };

  const updateItem = (lineId, patch) => {
    setItems((prev) =>
      prev.map((it) => (it.lineId === lineId ? { ...it, ...patch } : it))
    );
  };

  const clearCart = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((sum, it) => sum + Number(it.price || 0) * Number(it.travelers || 1), 0),
    [items]
  );

  const value = {
    items,
    count: items.length,
    subtotal,
    addItem,
    removeItem,
    updateItem,
    clearCart,
  };

  return (
    <ExperienceCartContext.Provider value={value}>
      {children}
    </ExperienceCartContext.Provider>
  );
}

export const useExperienceCart = () => {
  const ctx = useContext(ExperienceCartContext);
  if (!ctx) {
    throw new Error("useExperienceCart must be used within an ExperienceCartProvider");
  }
  return ctx;
};
